import { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, Users, Bell, Palette, Mail, LogOut, Shield, Menu, X, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin/contacts', icon: Mail, label: 'Contact Messages' },
  { path: '/admin/bugs', icon: Bug, label: 'Bug Reports' },
  { path: '/admin/settings', icon: Palette, label: 'Settings' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div className="min-h-screen bg-navy-950 flex">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-navy-900 border-r border-white/5 flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-white/5 flex items-center gap-3">
          <Shield className="text-red-400" size={24} />
          <span className="font-heading text-xl text-white">Admin Panel</span>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const active = item.end ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${active ? 'bg-cyan/10 text-cyan' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5 space-y-3">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2 text-sm text-slate-400 hover:text-white">
            <LayoutDashboard size={16} /> Back to App
          </Link>
          <button onClick={logout} className="flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:text-red-300 w-full">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-white/5 bg-navy-900/50 backdrop-blur-lg flex items-center px-6 gap-4 sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="lg:hidden text-white" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
          <div className="flex-1" />
          <span className="text-sm text-slate-400">Logged in as <span className="text-cyan">{user?.name}</span></span>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Outlet context={{ stats, refreshStats: fetchStats }} />
        </main>
      </div>
    </div>
  );
}
